import { motion } from "motion/react";
import { Command } from "./components/command.js";
import { usePrefersReducedMotion } from "./flow/reduced-motion.js";

export type SetupStepId = "project" | "repository" | "people" | "telegram" | "agent";
/** Every unfinished step opens exactly one dialog the cockpit already owns. */
export type SetupActionKind = "createProject" | "repository" | "invite" | "telegram";

export interface SetupStep {
  id: SetupStepId;
  title: string;
  /** One line, shown while the step is open. */
  note: string;
  /** One line, shown once the step is ticked. */
  doneNote: string;
  done: boolean;
  /** Why the button is not offered here: no project yet, not an admin, or no relay on this server. */
  blocked: string | null;
  action: { kind: SetupActionKind; label: string } | null;
  /** The one command that finishes this step in a terminal. */
  command: string | null;
}

export interface SetupSnapshot {
  projectCount: number;
  /** A project graph is in hand; until it is, nothing about it can be claimed. */
  projectLoaded: boolean;
  /** Repositories linked to the selected project. */
  repositoryCount: number;
  /** Members of the selected project other than the viewer that are invited or active. */
  otherMemberCount: number;
  /** null while the relay state is still unread; `available: false` on a server with no relay. */
  telegram: { available: boolean; linked: boolean } | null;
  /** The viewer's own agents and whether a terminal client is bound to each right now. */
  ownAgents: Array<{ name: string; opened: boolean }>;
  /** Repositories the viewer could still link; 0 means the button would open an empty picker. */
  linkableRepositoryCount: number;
  /** The viewer administers the selected project, so linking and inviting are theirs to do. */
  canAdmin: boolean;
}

/** The agent the last step names: a real one the person made, else the general agent. */
function commandAgentName(ownAgents: SetupSnapshot["ownAgents"]): string {
  return ownAgents.find((agent) => agent.name !== "general")?.name ?? ownAgents[0]?.name ?? "general";
}

/**
 * The checklist is derived, never stored: every tick is read back out of the data the
 * dashboard already loaded, so it stays true when someone else does a step for you.
 */
export function deriveSetupSteps(snapshot: SetupSnapshot): SetupStep[] {
  const hasProject = snapshot.projectCount > 0;
  // Until a project is open there is no membership to read, so admin is not yet a fact.
  const noProject = !hasProject ? "Create a project first." : snapshot.projectLoaded ? null : "Open a project first.";
  const notAdmin = snapshot.canAdmin ? null : "A project admin does this one.";
  const relayOff = snapshot.telegram && !snapshot.telegram.available ? "This server has no Telegram relay." : null;
  return [
    {
      id: "project",
      title: "Create a project",
      note: "Repositories, people and agents all hang off one project.",
      doneNote: "Your canvas is ready.",
      done: hasProject,
      blocked: null,
      action: { kind: "createProject", label: "New project" },
      command: null,
    },
    {
      id: "repository",
      title: "Link a repository",
      note: "Agents work inside repositories you can already reach on GitHub.",
      doneNote: `${snapshot.repositoryCount} linked.`,
      done: snapshot.repositoryCount > 0,
      blocked: noProject ?? notAdmin ?? (snapshot.linkableRepositoryCount === 0 ? "No other repository you can reach." : null),
      action: { kind: "repository", label: "Link a repository" },
      command: null,
    },
    {
      id: "people",
      title: "Invite someone",
      note: "One other person turns the canvas into a shared place.",
      doneNote: `${snapshot.otherMemberCount} other ${snapshot.otherMemberCount === 1 ? "person" : "people"} on the project.`,
      done: snapshot.otherMemberCount > 0,
      blocked: noProject ?? notAdmin,
      action: { kind: "invite", label: "Open People" },
      command: null,
    },
    {
      id: "telegram",
      title: "Connect Telegram",
      note: "Your agents reach you on your phone when they need a decision.",
      doneNote: "Your chat is linked.",
      done: snapshot.telegram?.linked === true,
      blocked: relayOff,
      action: { kind: "telegram", label: "Connect Telegram" },
      command: null,
    },
    {
      id: "agent",
      title: "Open an agent",
      note: "Run this in the terminal you just connected.",
      doneNote: "A terminal is attached.",
      done: snapshot.ownAgents.some((agent) => agent.opened),
      blocked: null,
      action: null,
      command: `baton open ${commandAgentName(snapshot.ownAgents)}`,
    },
  ];
}

/** How much is left, for the rail's badge and the card's own header. */
export function setupProgress(steps: SetupStep[]): { done: number; total: number; remaining: number } {
  const done = steps.filter((step) => step.done).length;
  return { done, total: steps.length, remaining: steps.length - done };
}

export interface SetupChecklistProps {
  steps: SetupStep[];
  onAction(kind: SetupActionKind): void;
  onDismiss(): void;
}

/** A small card on the canvas, not a wizard: five live steps, one button each. */
export function SetupChecklist(props: SetupChecklistProps) {
  const reduced = usePrefersReducedMotion();
  const progress = setupProgress(props.steps);
  return <section className="setup-checklist" data-testid="setup-checklist" aria-label="Set up Baton">
    <header className="setup-head">
      <p className="eyebrow">Set up</p>
      <span className="setup-progress" data-testid="setup-progress">{progress.done} of {progress.total}</span>
      <button type="button" className="setup-dismiss" aria-label="Hide the setup checklist" onClick={props.onDismiss}>×</button>
    </header>
    <ol className="setup-steps">
      {props.steps.map((step) => {
        const action = step.done || step.blocked ? null : step.action;
        return <li
          key={step.id}
          className="setup-step"
          data-testid="setup-step"
          data-step={step.id}
          data-done={step.done ? "true" : "false"}
        >
          <SetupTick done={step.done} reduced={reduced} />
          <div className="setup-step-main">
            <p className="setup-step-title">{step.title}</p>
            <p className="setup-step-note">{step.done ? step.doneNote : step.blocked ?? step.note}</p>
            {!step.done && step.command && <Command label="In your terminal" value={step.command} />}
            {action && <button
              type="button"
              className="setup-step-action"
              data-testid="setup-step-action"
              onClick={() => props.onAction(action.kind)}
            >{action.label}</button>}
          </div>
        </li>;
      })}
    </ol>
  </section>;
}

/** The tick is the only thing that moves: a single settle when a step turns true. */
function SetupTick({ done, reduced }: { done: boolean; reduced: boolean }) {
  return <motion.span
    className={`setup-tick${done ? " done" : ""}`}
    aria-hidden="true"
    initial={false}
    animate={reduced ? { scale: 1 } : { scale: done ? [1, 1.16, 1] : 1 }}
    transition={{ duration: reduced ? 0 : 0.26, ease: [0.4, 0, 0.2, 1] }}
  >{done ? "✓" : ""}</motion.span>;
}
